import { styled } from '@mui/material/styles';
import type {
  DataGridProProps,
  GridColumnResizeParams,
} from '@mui/x-data-grid-pro';
import { GRID_TREE_DATA_GROUPING_FIELD } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import type { DataGroup } from '@/widgets/TreeDataGrid';
import { useTreeDataGridRowsExpansionRestoreProps } from '@/widgets/TreeDataGrid';
import type { ColumnsConfig } from '@/entities/columnsConfig';
import { DimensionColumnDataType } from '@/entities/columnsConfig';
import type { RowHeights } from '@/shared/constants';
import { Analytics } from '@/shared/lib';
import type { TotalData } from '@/shared/ui';
import { DataGrid as SharedDataGrid } from '@/shared/ui';
import type { Highlight, TreeDataResponse } from '../../model/types';
import { getRowHeight } from '../DataTable/getRowHeight';
import { useData } from './useData';
import { useTreeDataGridProps } from './useTreeDataGridProps';

const INITIAL_STATE: DataGridProProps['initialState'] = {
  pinnedColumns: {
    left: [GRID_TREE_DATA_GROUPING_FIELD],
  },
};

const getRowClassName = (): DataGridProProps['getRowClassName'] => (params) => {
  const classes = [];

  if (params.row.isTotal) {
    classes.push('total');
  }

  if (params.row.firstInGroup) {
    classes.push('firstRowInGroup');
  }
  if (params.row.lastInGroup) {
    classes.push('lastRowInGroup');
  }

  return classes.join(' ');
};

const DataGrid = styled(SharedDataGrid)(() => ({
  '& .MuiDataGrid-treeDataGroupingCell': {
    width: 'unset',

    '& > span': {
      minWidth: 0,
    },
  },

  '& .denseRow .MuiDataGrid-treeDataGroupingCellToggle': {
    marginRight: 8,

    '& button': {
      padding: 1,
    },
  },

  '& .groupingCell, & .groupingHeader': {
    borderRight: '1px solid #e0e0e0',
  },

  '& .total+.firstRowInGroup .MuiDataGrid-cell': {
    borderTop: '1px solid var(--rowBorderColor)',
  },

  '& .firstRowInGroup .MuiDataGrid-cell': {
    borderTop: '1px solid #9e9e9e',
  },

  '& .lastRowInGroup+.firstRowInGroup .MuiDataGrid-cell': {
    borderTop: 'none',
  },

  '& .lastRowInGroup+.MuiDataGrid-row .MuiDataGrid-cell': {
    borderTop: 'none',
  },

  '& .lastRowInGroup .MuiDataGrid-cell': {
    borderBottom: '1px solid #9e9e9e',
  },
}));

interface Props {
  columnsWidth: Record<string, number | undefined>;
  columnsConfig: ColumnsConfig;
  rowHeight: RowHeights;
  dimensions: string[];
  metric: string;
  highlight: Highlight;
  getData: (dataGroups: DataGroup[]) => Promise<TreeDataResponse>;
  getTotal: () => Promise<TotalData>;
  updateColumnsWidth: (columnName: string, newWidth?: number) => void;
}

export function TreeDataGrid({
  columnsConfig,
  dimensions,
  rowHeight,
  metric,
  columnsWidth,
  highlight,
  updateColumnsWidth,
  getData,
  getTotal,
}: Props) {
  const mediaDimensions = useMemo(() => {
    return dimensions.filter((dimension) => {
      return columnsConfig[dimension].type === DimensionColumnDataType.MEDIA;
    });
  }, [dimensions, columnsConfig]);
  const { dataSource, dataColumns, total } = useData({
    dimensions,
    mediaDimensions,
    getData,
    getTotal,
  });
  const { getTreeDataPath, columns, groupingColDef } = useTreeDataGridProps({
    total,
    rowHeight,
    columnsConfig,
    columnsWidth,
    dimensions,
    metric,
    highlight,
    dataColumns,
  });
  const { isGroupExpandedByDefault } =
    useTreeDataGridRowsExpansionRestoreProps();

  const handleColumnWidthChange = useCallback(
    (params: GridColumnResizeParams) => {
      const { width, field } = params.colDef;

      updateColumnsWidth(field, width);
      Analytics.sendFeatureUsage('tree_data_grid', 'resize_column', {
        page: window.location.pathname,
      });
    },
    [updateColumnsWidth],
  );

  return (
    <DataGrid
      treeData
      initialState={INITIAL_STATE}
      getRowHeight={getRowHeight({ rowHeight, highlight })}
      isGroupExpandedByDefault={isGroupExpandedByDefault}
      getRowClassName={getRowClassName()}
      columns={columns}
      groupingColDef={groupingColDef}
      getTreeDataPath={getTreeDataPath}
      unstable_dataSource={dataSource}
      onColumnWidthChange={handleColumnWidthChange}
    />
  );
}
